import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import useLabels from "hooks/useLabels";

interface UseDragAndDropUploadResult {
  isDragging: boolean;
}

interface UseDragAndDropUploadOptions {
  onFileDropped: (file: File) => void;
}

/**
 * Hook that handles global drag-and-drop detection.
 * Listens for PDF files being dragged anywhere on the document and
 * calls the provided callback when a file is dropped.
 */
export const useDragAndDropUpload = ({
  onFileDropped,
}: UseDragAndDropUploadOptions): UseDragAndDropUploadResult => {
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);
  const [labels] = useLabels("features.threads.data-source-selector");

  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    dragCounterRef.current++;

    // Check if dragging files (not text or other content)
    if (e.dataTransfer?.types.includes("Files")) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    dragCounterRef.current--;
    if (dragCounterRef.current <= 0) {
      setIsDragging(false);
      dragCounterRef.current = 0;
    }
  }, []);

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      setIsDragging(false);
      dragCounterRef.current = 0;

      const files = Array.from(e.dataTransfer?.files || []);
      const pdfFile = files.find((file) => file.type === "application/pdf");

      if (pdfFile) {
        onFileDropped(pdfFile);
      } else if (files.length > 0) {
        toast.error(labels["drag-filetype-error"]);
      }
    },
    [onFileDropped, labels]
  );

  useEffect(() => {
    // Add global document listeners
    document.addEventListener("dragenter", handleDragEnter);
    document.addEventListener("dragleave", handleDragLeave);
    document.addEventListener("dragover", handleDragOver);
    document.addEventListener("drop", handleDrop);

    return () => {
      // Cleanup listeners
      document.removeEventListener("dragenter", handleDragEnter);
      document.removeEventListener("dragleave", handleDragLeave);
      document.removeEventListener("dragover", handleDragOver);
      document.removeEventListener("drop", handleDrop);
    };
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);

  return {
    isDragging,
  };
};
